import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArqueoPayment,
  ArticleInfo,
  ARTICLES_CATEGORY_LABEL,
  getCustomerName,
  getPaymentTime,
} from "@/lib/arqueo/aggregations";

interface Props {
  payments: ArqueoPayment[];
  articlesById: Map<string, ArticleInfo>;
  isLoading: boolean;
}

const fmtMoney = (n: number) =>
  `$${Number(n || 0).toLocaleString("es-AR", { maximumFractionDigits: 2 })}`;

const getCategoryLabel = (p: ArqueoPayment): string => {
  if (p.is_article_sale) return ARTICLES_CATEGORY_LABEL;
  return p.description?.split(",")[0]?.trim() || "—";
};

const getArticleLabel = (
  p: ArqueoPayment,
  articlesById: Map<string, ArticleInfo>
): string => {
  if (!p.is_article_sale) return "—";
  if (p.article_id) {
    const info = articlesById.get(p.article_id);
    if (info) return info.name;
  }
  return p.description || "Artículo";
};

export const ArqueoTable = ({ payments, articlesById, isLoading }: Props) => {
  if (isLoading) {
    return (
      <Card className="p-4 bg-card/60 backdrop-blur border-primary/10">
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </Card>
    );
  }

  if (payments.length === 0) {
    return (
      <Card className="p-8 text-center bg-card/60 backdrop-blur border-primary/10 text-primary/60">
        No se registraron pagos para los filtros seleccionados.
      </Card>
    );
  }

  return (
    <>
      {/* Mobile: card list */}
      <div className="md:hidden space-y-2">
        {payments.map((p) => {
          const article = getArticleLabel(p, articlesById);
          return (
            <Card
              key={p.id}
              className="p-3 bg-card/60 backdrop-blur border-primary/10"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-primary truncate">
                    {getCustomerName(p)}
                  </div>
                  <div className="text-xs text-primary/70 mt-0.5 truncate">
                    {getCategoryLabel(p)}
                    {article !== "—" && (
                      <span className="text-primary/80"> · {article}</span>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-bold text-primary tabular-nums">
                    {fmtMoney(Number(p.amount) || 0)}
                  </div>
                  <div className="text-[10px] text-primary/60 tabular-nums">
                    {getPaymentTime(p)}
                  </div>
                </div>
              </div>
              <div className="mt-2">
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary/80 border border-primary/15">
                  {p.payment_method || "Sin método"}
                </span>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Desktop / tablet: table */}
      <Card className="hidden md:block bg-card/60 backdrop-blur border-primary/10 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Cliente</TableHead>
              <TableHead>Método</TableHead>
              <TableHead>Categoría</TableHead>
              <TableHead>Artículo</TableHead>
              <TableHead className="text-right">Monto</TableHead>
              <TableHead className="text-right">Hora</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="font-medium">{getCustomerName(p)}</TableCell>
                <TableCell>{p.payment_method || "—"}</TableCell>
                <TableCell>{getCategoryLabel(p)}</TableCell>
                <TableCell>{getArticleLabel(p, articlesById)}</TableCell>
                <TableCell className="text-right font-semibold">
                  {fmtMoney(Number(p.amount) || 0)}
                </TableCell>
                <TableCell className="text-right tabular-nums text-primary/70">
                  {getPaymentTime(p)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </>
  );
};
